import React, { useContext, useEffect, useState } from 'react';
import UserPermissionsPanel from '../../../global/ui/UserPermissionsPanel';
import AuditTimeline from './AuditTimeLine';
import { makeDeleteCall, makeGetCall, makeNextPageCall, makePatchCall } from '../../../effects/requests';
import { endPoints } from '../../../utils/endpoints';
import { emptyFunction, getCompanyName, showToast } from '../../../utils';
import { ApiResponseType } from '../../../types';
import UsersTable from './UsersTable';
import moment from 'moment';
import InfiniteScroll from 'react-infinite-scroll-component';
import Loader from '../../../global/ui/Loader';
import RenderContainer from './RenderContainer';
import { MyContext } from '../../../context';

const UsersAudit = ({ locationId, locationData, type }: any) => {

    const { userData } = useContext(MyContext)

    const [activeTab, setActiveTab] = useState('users'); // 'users' | 'audit'
    const [users, setUsers] = useState([]);
    const [auditData, setAuditData] = useState([]);
    const [nextUrl, setNextUrl] = useState(null);
    const [auditNextUrl, setAuditNextUrl] = useState(null);
    const [query, setQuery] = useState('');
    const [panelUser, setPanelUser] = useState(null);
    const [filterOpen, setFilterOpen] = useState(false);

    const updateUserField = (item: any, field: any, value: any) => {
        const payload: any = {};
        if (field === 'accessLevel') {
            payload.access_level = value;
        }
        if (field === 'locationAccess') {
            payload.can_view_location = value == "Yes";
        }
        if (field === 'expiryDate') {
            payload.expiry_date = value === "Never" ? null : moment(value, 'DD/MM/YY').format('YYYY-MM-DD')
        }

        const url = `${endPoints.LOCATION_VAULT_ACCESS}/${item?.id}/`

        makePatchCall(url, payload).then((res: ApiResponseType<any>) => {
            if (res?.status?.code === 200) {
                const updatedUsers: any = users?.map((user: any) => user?.id === item?.id ? res?.data : user);
                setUsers(updatedUsers);
                if (panelUser) setPanelUser(res?.data)
            }
        }).catch(emptyFunction)
    };

    const onRemove = (item: any) => {
        const url = `${endPoints.LOCATION_VAULT_ACCESS}/${item?.id}/`
        makeDeleteCall(url).then((res: ApiResponseType<any>) => {
            if (res?.status?.code === 200) {
                showToast(res?.status?.message || 'Deleted successfully!', 'success')
                setPanelUser(null);
                fetchUser();
            }
        }).catch(emptyFunction)
    }

    const openPanelFor = (user: any) => setPanelUser(user);

    const handleDownloadAuditLog = () => {
        console.log('Download audit log', auditData);
    };

    const fetchUser = () => {
        makeGetCall(endPoints.LOCATION_VAULT_ACCESS, { location_vault_id: locationId, production_company_name: getCompanyName(userData), }).then((res: ApiResponseType<any>) => {
            if (res?.status?.code == 200) {
                setUsers(res?.results?.length > 0 ? res?.results : []);
                setNextUrl(res?.next || null)
            }
        }).catch(emptyFunction)
    }

    const formatAuditTimestamp = (dateString: any) => {
        if (!dateString) return '';

        const date = new Date(dateString);

        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        const isSameDay = (a: any, b: any) =>
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate();

        const time = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });

        if (isSameDay(date, today)) {
            return `Today ${time}`;
        }

        if (isSameDay(date, yesterday)) {
            return `Yesterday ${time}`;
        }

        return `${date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })} ${time}`;
    };

    const mapAuditLog = (item: any) => {
        return {
            id: item.id,
            actionType: item.action_type,
            timestamp: formatAuditTimestamp(item.created_at),
            user: item.actor_name || 'Unknown User',
            role: item.actor_role || '',
            description: item.summary || '',
            notes: item.notes || null,
            extraLines: item.notes ? [item.notes] : [],
            extra_data: item
        };
    };

    const fetchAudit = () => {
        const params = {
            location_vault_id: locationId,
            production_company_name: getCompanyName(userData),
        }
        makeGetCall(endPoints.LOCATION_VAULT_AUDIT, params).then((res: ApiResponseType<any>) => {
            if (res?.status?.code == 200) {
                const data = res?.results?.length > 0 ? res?.results : [];
                if (data?.length > 0) {
                    const result = data.map(mapAuditLog);
                    setAuditData(result);
                    setAuditNextUrl(res?.next || null)
                    return
                }
                setAuditData([]);
            }
        }).catch(emptyFunction)
    }

    const fetchAuditMoreData = () => {
        if (!auditNextUrl) return;
        makeNextPageCall(auditNextUrl, false, true).then((res: ApiResponseType<any>) => {
            const data = res?.results?.length > 0 ? res?.results : [];
            const result = data?.map(mapAuditLog);
            const finalResult: any = [...auditData, ...result];
            setAuditData(finalResult);
            setAuditNextUrl(res?.next || null)
        })
    }

    const fetchMoreData = () => {
        if (!nextUrl) return;
        makeNextPageCall(nextUrl, false, true).then((res: ApiResponseType<any>) => {
            const data: any = [...users, ...res.results];
            setUsers(data);
            setNextUrl(res?.next);
        })
    }

    useEffect(() => {
        if (locationData?.role != 'V' && userData?.id) {
            fetchUser();
            fetchAudit()
        }
    }, [userData?.id])

    return (
        <RenderContainer type={type}>
            {/* Header */}
            {type == 'list' &&
                <div className="ua-header">
                    <h1 className="ua-header__title">Users &amp; Audit</h1>

                    <div className="ua-header__actions">
                        <div className="ua-search">
                            <input
                                type="text"
                                placeholder="Search for user"
                                value={query}
                                onChange={e => setQuery(e.target.value)}
                            />
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round">
                                <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                        </div>
                        {/* 
                        <button type='button' className="ua-add-btn">
                            Add User
                            <span className="ua-add-btn__icon">
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                </svg>
                            </span>
                        </button> */}
                    </div>
                </div>
            }

            {/* Tabs + right-side action */}
            <div className="ua-bar">
                <div className="ua-tabs">
                    <button
                        className={`ua-tab${activeTab === 'users' ? ' ua-tab--active' : ''}`}
                        onClick={() => setActiveTab('users')}
                    >
                        Users
                    </button>
                    <button
                        className={`ua-tab${activeTab === 'audit' ? ' ua-tab--active' : ''}`}
                        onClick={() => setActiveTab('audit')}
                    >
                        Audit Log
                    </button>
                </div>

                <div className="ua-bar__right">
                    {activeTab === 'audit' && (
                        <button className="ua-icon-only-btn" onClick={() => setFilterOpen(false)} aria-label="Filter">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                            </svg>
                        </button>
                    )}

                    {activeTab === 'users' ? (
                        <button className="ua-filter-btn" onClick={() => setFilterOpen(false)}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                            </svg>
                            Filter
                        </button>
                    ) : (
                        <button style={{ display: locationData?.role != 'V' ? 'block' : 'none' }} className="ua-filter-btn" onClick={handleDownloadAuditLog}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            Download
                        </button>
                    )}
                </div>
            </div>
            {activeTab === 'users' ? (
                <InfiniteScroll
                    dataLength={users?.length}
                    next={fetchMoreData}
                    hasMore={nextUrl ? true : false}
                    loader={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader imgStyle={{ width: '40px', height: '40px' }} /></div>}
                // scrollableTarget="scrollableId"
                >
                    <UsersTable
                        users={users}
                        onChangeField={updateUserField}
                        onEdit={openPanelFor}
                    />

                </InfiniteScroll>
            ) : (
                <div className="ua-audit-wrap">
                    <InfiniteScroll
                        dataLength={auditData?.length}
                        next={fetchAuditMoreData}
                        hasMore={auditNextUrl ? true : false}
                        loader={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Loader imgStyle={{ width: '40px', height: '40px' }} /></div>}
                    // scrollableTarget="scrollableId"
                    >
                        <AuditTimeline
                            entries={auditData}
                        />
                    </InfiniteScroll>
                </div>
            )}

            {/* User profile + permissions side panel (dropdown-only editing) */}
            {panelUser && (
                <UserPermissionsPanel
                    user={panelUser}
                    auditEntries={auditData as never}
                    onClose={() => setPanelUser(null)}
                    onChangeField={updateUserField}
                    onRemove={onRemove}
                />
            )}

            {/* Filter side panel — shared by Users and Audit Log */}
            {/* {filterOpen && (
                        <FilterPanel
                            onClose={() => setFilterOpen(false)}
                            onApply={(filters) => { console.log('apply filters', filters); setFilterOpen(false); }}
                            userOptions={users.map(u => u.name)}
                            roleOptions={[...new Set(users.map(u => u.role))]}
                            departmentOptions={[...new Set(users.map(u => u.department))]}
                            crewGroupOptions={['Crew Team A', 'Crew Team B', 'Crew Team C']}
                        />
                    )} */}
        </RenderContainer>
    );
};

export default UsersAudit;